import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Maximize2, Minimize2, FileText, Video, BookOpen, Layout } from 'lucide-react';

interface Material {
  id: string;
  name: string;
  description?: string;
  type: 'pdf' | 'video' | 'guide' | 'template';
  fileUrl: string;
  fileSize?: string;
}

interface MaterialViewerProps {
  material: Material | null;
  onClose: () => void;
}

export default function MaterialViewer({ material, onClose }: MaterialViewerProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!material) return null;

  const getIcon = () => {
    switch (material.type) {
      case 'pdf':
        return <FileText size={20} />;
      case 'video':
        return <Video size={20} />;
      case 'guide':
        return <BookOpen size={20} />;
      case 'template':
        return <Layout size={20} />;
      default:
        return <FileText size={20} />;
    }
  };

  const getTypeColor = () => {
    switch (material.type) {
      case 'pdf':
        return '#ef4444';
      case 'video':
        return '#8b5cf6';
      case 'guide':
        return '#3b82f6';
      case 'template':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const handleDownload = () => {
    window.open(material.fileUrl, '_blank');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const renderContent = () => {
    if (material.type === 'pdf') {
      return (
        <iframe
          src={`${material.fileUrl}#toolbar=1&navpanes=0&scrollbar=1`}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: isFullscreen ? 0 : '0 0 12px 12px',
          }}
          title={material.name}
        />
      );
    }

    if (material.type === 'video') {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#000',
            borderRadius: isFullscreen ? 0 : '0 0 12px 12px',
          }}
        >
          <video
            controls
            autoPlay
            playsInline
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
            }}
          >
            <source src={material.fileUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    // For guides and templates, show an iframe or fallback message
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1.5rem',
          padding: '2rem',
          background: '#f9fafb',
          borderRadius: isFullscreen ? 0 : '0 0 12px 12px',
        }}
      >
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: `${getTypeColor()}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: getTypeColor(),
          }}
        >
          {getIcon()}
        </div>
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#0f172a' }}>{material.name}</h3>
          {material.description && (
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.9375rem' }}>
              {material.description}
            </p>
          )}
        </div>
        <button
          onClick={handleDownload}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'var(--accent, #dc2626)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9375rem',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Download size={18} />
          Download {material.type.charAt(0).toUpperCase() + material.type.slice(1)}
        </button>
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isFullscreen ? 0 : '1rem',
        }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: isFullscreen ? '100%' : '90%',
            height: isFullscreen ? '100%' : '90%',
            maxWidth: isFullscreen ? '100%' : '1200px',
            maxHeight: isFullscreen ? '100%' : '800px',
            background: 'white',
            borderRadius: isFullscreen ? 0 : '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: isFullscreen ? 'none' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: `${getTypeColor()}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: getTypeColor(),
                }}
              >
                {getIcon()}
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>
                  {material.name}
                </h2>
                {material.fileSize && (
                  <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                    {material.fileSize}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={handleDownload}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  fontSize: '0.875rem',
                  color: '#374151',
                }}
                title="Download"
              >
                <Download size={16} />
                Download
              </button>
              <button
                onClick={toggleFullscreen}
                style={{
                  padding: '0.5rem',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#374151',
                }}
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: '0.5rem',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#374151',
                }}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {renderContent()}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
